import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  AMOUNT_FORMAT_TYPES,
  AMOUNT_TYPES,
  AmountType,
} from '@ezugi/constants';
import { TableChip } from '@ezugi/types';
import { ChipSelector, ChipSelectorProps } from '@ezugi/ui/components';
import { formatAmount } from '@ezugi/utils';

import { gameActions } from '../../store/actions';
import { FormatConfig } from '../../store/reducers/config';
import { flyingChipsActions } from '../../store/reducers/flyingChips';
import {
  canPlaceBetsSelector,
  chipsSelector,
  formatSelector,
  selectedChipSelector,
  userBalanceSelector,
} from '../../store/selectors';

const DEFAULT_TYPE = AMOUNT_TYPES.CHIP;
const DEFAULT_FORMAT = {
  betFormat: AMOUNT_FORMAT_TYPES.EXTEND_LETTER,
  kSymbol: 'K',
  mSymbol: 'M',
  limitFormat: AMOUNT_FORMAT_TYPES.COMMA,
};

export const getDisplayValue = (
  value: number,
  type: AmountType = DEFAULT_TYPE,
  format: FormatConfig = DEFAULT_FORMAT
) => formatAmount(value, type, format, null, false, false);

export type BootstrapChipSelectorProps = Omit<ChipSelectorProps, 'balance' | 'chips'>

const BootstrapChipSelector = React.memo(
  ({ onChange, selected, ...props }: BootstrapChipSelectorProps) => {
    const canPlaceBets = useSelector(canPlaceBetsSelector);
    const chips = useSelector(chipsSelector);
    const selectedChipFromState = useSelector(selectedChipSelector);
    const balance = useSelector(userBalanceSelector);
    const format = useSelector(formatSelector);

    const dispatch = useDispatch();

    const selectedChip = useMemo(
      () => selected || selectedChipFromState,
      [ selected, selectedChipFromState ]
    );

    const selectChip = useCallback(
      (chip: TableChip) => {
        if (chip.value > balance) {
          return;
        }

        if (onChange) {
          onChange(chip);
        } else if (selectedChip?.value !== chip.value) {
          dispatch(gameActions.selectChip.request(chip));
        }
      },
      [ dispatch, selectedChip, onChange, balance ]
    );

    const chipDisplayFn = useCallback(
      (value: number) => getDisplayValue(value, AMOUNT_TYPES.CHIP, format),
      [ format ]
    );

    const handleToggle = useCallback(
      () => {
        dispatch(flyingChipsActions.removeAllFlyingChips());
      },
      [ dispatch ],
    );

    return (
      <ChipSelector
        chips={chips}
        balance={balance}
        selected={selectedChip}
        onChange={selectChip}
        disabled={!canPlaceBets}
        chipDisplay={chipDisplayFn}
        handleToggle={handleToggle}
        {...props}
      />
    );
  }
);

export default BootstrapChipSelector;
